import { filter } from "lodash";
import React from "react";
import { StyleSheet, TouchableOpacity, TouchableOpacityProps, View } from "react-native";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { CheckMarkBoxAnimated } from "../impComponents";
import { PdText, tPdText } from "../layout";

export type tPdCheckbox = {
  value?: boolean;
  onChange?: (value: boolean) => void;
  isDisabled?: boolean;
} & TouchableOpacityProps;

export type tPdCheckboxOption = { value: string; label: string };

export type tPdCheckboxGroup = {
  options: tPdCheckboxOption[];
  value?: string[];
  onChange?: (value: string[]) => void;
  isDisabled?: boolean;
} & TouchableOpacityProps;

const PdCheckbox = React.forwardRef<TouchableOpacity, React.PropsWithChildren<tPdCheckbox>>((props, ref) => {
  const { value, isDisabled, style, onChange, children, ...rest } = props;

  const handleOnPress = () => {
    if (!isDisabled) {
      onChange?.(!value);
    }
  };

  const hasChildren = React.Children.count(children) > 0;

  return (
    <TouchableOpacity
      ref={ref}
      onPress={handleOnPress}
      style={[styles.pdCheckboxContainer, isDisabled ? styles.opacityMd : {}, style]}
      disabled={isDisabled}
      {...rest}
    >
      <CheckMarkBoxAnimated
        style={[styles.pdCheckbox, hasChildren ? styles.mr2 : {}]}
        mystate={value ? "checked" : "unchecked"}
      />
      {children}
    </TouchableOpacity>
  );
});
PdCheckbox.displayName = "PdCheckbox";

export const PdCheckboxGroup = React.forwardRef<TouchableOpacity, React.PropsWithChildren<tPdCheckboxGroup>>(
  (props, ref) => {
    const { options, value, onChange, isDisabled, style, ...rest } = props;

    const handleOnPress = (option: tPdCheckboxOption) => {
      const exists = value?.includes(option.value);
      if (exists) onChange?.(value ? filter(value, (v) => v !== option.value) : []);
      else onChange?.(value ? [...value, option.value] : []);
    };

    return (
      <View style={[styles.pdCheckboxGroupContainer, style]}>
        {options.map((option) => {
          const isChecked = value?.includes(option.value);
          const _isDisabled = isDisabled && !isChecked;
          return (
            <TouchableOpacity
              ref={ref}
              key={option.value}
              onPress={() => (_isDisabled ? undefined : handleOnPress(option))}
              style={[styles.pdCheckboxContainer, _isDisabled ? styles.opacityMd : {}]}
              disabled={_isDisabled}
              {...rest}
            >
              <CheckMarkBoxAnimated
                style={[styles.pdCheckbox, styles.mr2]}
                mystate={isChecked ? "checked" : "unchecked"}
              />
              <PdCheckboxLabel>{option.label}</PdCheckboxLabel>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  },
);

const PdCheckboxLabel: React.FC<tPdText> = ({ style, ...rest }) => {
  return <PdText style={[styles.pdCheckboxLabel, style]} {...rest} />;
};
PdCheckboxLabel.displayName = "PdCheckboxLabel";

export { PdCheckbox, PdCheckboxLabel };

const styles = StyleSheet.create({
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  opacityMd: {
    opacity: 0.4,
  },
  pdCheckbox: {
    height: 15,
    width: 15,
  },
  pdCheckboxContainer: {
    alignItems: "center",
    flexDirection: "row",
  },
  pdCheckboxGroupContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 3 * kStyleConst.kSpacing,
  },
  pdCheckboxLabel: {
    color: kColors.brandDark,
    fontSize: 11,
    fontWeight: "400",
    letterSpacing: 1,
    textTransform: "uppercase",
  },
});
